import { useEffect, useState } from "react";
import { Page } from "@iq/models";
import VariableTicketContainer from "../../components/VariableTicketContainer/VariableTicketContainer";
import { getIncidentContext } from "../../services/incident_context_service";
import "./PortalRequestDetail.css";

function formatTimestamp(value) {
	if (!value) {
		return "-";
	}
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function LifecycleTicketLinks({ tickets, type, loading, error, onOpen }) {
	if (tickets.length === 0) {
		return <small title={error || undefined}>{loading ? "Loading..." : error ? "Unavailable" : "As needed"}</small>;
	}

	return (
		<div className="it-portal-lifecycle-links">
			{tickets.map((ticket) => {
				const identifier = ticket?.number || `${type} #${ticket?.id ?? "?"}`;
				return (
					<button
						key={ticket?.id || identifier}
						type="button"
						className="it-portal-lifecycle-link"
						onClick={() => onOpen?.(ticket)}
						disabled={!onOpen}
						aria-label={`Open ${type.toLowerCase()} ${identifier}`}
					>
						<span>{identifier}</span>
						<small>{ticket?.status || "Open"}</small>
					</button>
				);
			})}
		</div>
	);
}

function LifecycleOverview({ request, context, loading, error, onOpenIncident, onOpenProblem, onOpenChange }) {
	const hasIncident = Boolean(request?.incident?.id);
	const problems = Array.isArray(context?.problems) ? context.problems : [];
	const changes = Array.isArray(context?.related_changes) ? context.related_changes : [];
	const hasProblems = problems.length > 0;
	const hasChanges = changes.length > 0;
	const incidentIdentifier = request?.incident?.number || `Incident #${request?.incident?.id ?? "?"}`;

	return (
		<section className="it-portal-lifecycle" aria-label="Request lifecycle">
			<div className="it-portal-lifecycle-heading">
				<span>Lifecycle</span>
				<strong>{request?.status || "new"}</strong>
			</div>
			<ol className="it-portal-lifecycle-steps">
				<li className="is-complete">
					<span>1</span>
					<div><strong>Request</strong><small>{request?.status || "new"}</small></div>
				</li>
				<li className={hasIncident ? (hasProblems ? "is-complete" : "is-current") : ""}>
					<span>2</span>
					<div>
						<strong>Incident</strong>
						{hasIncident ? (
							<button
								type="button"
								className="it-portal-lifecycle-link"
								onClick={() => onOpenIncident?.(request.incident)}
								disabled={!onOpenIncident}
								aria-label={`Open incident ${incidentIdentifier}`}
							>
								<span>{incidentIdentifier}</span>
								<small>{request?.incident?.status || "Open"}</small>
							</button>
						) : <small>Pending</small>}
					</div>
				</li>
				<li className={hasProblems ? (hasChanges ? "is-complete" : "is-current") : ""}>
					<span>3</span>
					<div>
						<strong>Problem</strong>
						<LifecycleTicketLinks tickets={problems} type="Problem" loading={loading} error={error} onOpen={onOpenProblem} />
					</div>
				</li>
				<li className={hasChanges ? "is-current" : ""}>
					<span>4</span>
					<div>
						<strong>Change</strong>
						<LifecycleTicketLinks tickets={changes} type="Change" loading={loading} error={error} onOpen={onOpenChange} />
					</div>
				</li>
			</ol>
		</section>
	);
}

export default function PortalRequestDetail({
	initialRequest,
	clients = [],
	onConvert,
	onUpdateStatus,
	onOpenIncident,
	onOpenProblem,
	onOpenChange,
}) {
	const [request, setRequest] = useState(initialRequest || null);
	const [lifecycleContext, setLifecycleContext] = useState({ problems: [], related_changes: [] });
	const [lifecycleLoading, setLifecycleLoading] = useState(false);
	const [lifecycleError, setLifecycleError] = useState("");
	const [working, setWorking] = useState(false);
	const [error, setError] = useState("");
	const incidentId = request?.incident?.id;

	useEffect(() => {
		setRequest(initialRequest || null);
	}, [initialRequest]);

	useEffect(() => {
		let ignore = false;
		setLifecycleContext({ problems: [], related_changes: [] });
		setLifecycleError("");

		if (!incidentId) {
			setLifecycleLoading(false);
			return () => {
				ignore = true;
			};
		}

		setLifecycleLoading(true);
		getIncidentContext(incidentId)
			.then((context) => {
				if (!ignore) {
					setLifecycleContext(context);
				}
			})
			.catch((nextError) => {
				if (!ignore) {
					setLifecycleError(nextError?.message || "Failed to load linked tickets");
				}
			})
			.finally(() => {
				if (!ignore) {
					setLifecycleLoading(false);
				}
			});

		return () => {
			ignore = true;
		};
	}, [incidentId]);

	const client = clients.find((entry) => Number(entry?.id) === Number(request?.client_id));
	const allowedTransitions = Array.isArray(request?.allowed_transitions) ? request.allowed_transitions : [];
	const canConvert = !request?.incident?.id && allowedTransitions.includes("converted");
	const canCancel = allowedTransitions.includes("cancelled");
	const canClose = allowedTransitions.includes("closed");

	async function handleConvert() {
		if (!request?.id || !onConvert) {
			return;
		}
		setWorking(true);
		setError("");
		try {
			const payload = await onConvert(request.id);
			const updatedRequest = payload?.portal_request || request;
			const incident = payload?.incident || updatedRequest?.incident;
			setRequest(updatedRequest);
			if (incident?.id) {
				onOpenIncident?.(incident);
			}
		} catch (nextError) {
			setError(nextError?.message || "Failed to create incident");
		} finally {
			setWorking(false);
		}
	}

	async function handleStatus(status) {
		if (!request?.id || !onUpdateStatus) {
			return;
		}
		setWorking(true);
		setError("");
		try {
			const updatedRequest = await onUpdateStatus(request.id, status);
			setRequest(updatedRequest);
		} catch (nextError) {
			setError(nextError?.message || "Failed to update request");
		} finally {
			setWorking(false);
		}
	}

	const child1 = {
		title: request?.subject || "Untitled request",
		subtitle: request?.submitted_by_name || request?.submitted_by_email || "Unknown requester",
		pill: request?.status || "new",
		fields: [
			{ label: "Request", value: `REQ-${request?.id ?? ""}` },
			{ label: "Submitted", value: formatTimestamp(request?.created_at) },
			{ label: "Client", value: client?.name || (request?.client_id ? `Client #${request.client_id}` : "Unassigned") },
			{ label: "Incident", value: request?.incident?.number || "Not created" },
		],
	};

	const child2 = {
		inputs: [
			{ key: "requester", label: "Submitted By:", value: request?.submitted_by_name || "-", type: "text", disabled: true },
			{ key: "email", label: "Email:", value: request?.submitted_by_email || "-", type: "text", disabled: true },
			{ key: "subject", label: "Subject:", value: request?.subject || "-", type: "text", disabled: true, style: "grid-column: 1 / span 4;" },
			{ key: "message", label: "Request:", value: request?.message || "No message provided", type: "textarea", rows: 8, disabled: true, style: "grid-column: 1 / span 4;" },
		],
	};

	const primaryActions = [
		{
			key: "convert-request",
			label: working ? "Creating Incident..." : "Create Incident",
			onClick: handleConvert,
			disabled: working,
			visible: canConvert,
		},
		{
			key: "open-incident",
			label: "Open Incident",
			onClick: () => onOpenIncident?.(request?.incident),
			disabled: working,
			visible: Boolean(request?.incident?.id),
		},
	];

	const statusActions = [
		{ key: "cancel-request", label: "Cancel Request", onClick: () => handleStatus("cancelled"), disabled: working, variant: "secondary", visible: canCancel },
		{ key: "close-request", label: "Close Intake", onClick: () => handleStatus("closed"), disabled: working, visible: canClose },
	];

	return (
		<Page>
			{error ? <p className="it-portal-request-error">{error}</p> : null}
			<VariableTicketContainer
				title="Request Details"
				child1={child1}
				child1ActionButtons={primaryActions}
				child2={child2}
				actionButtons={statusActions}
				child3={(
					<LifecycleOverview
						request={request}
						context={lifecycleContext}
						loading={lifecycleLoading}
						error={lifecycleError}
						onOpenIncident={onOpenIncident}
						onOpenProblem={onOpenProblem}
						onOpenChange={onOpenChange}
					/>
				)}
			/>
		</Page>
	);
}